import ReactMarkdown from "react-markdown";
import { FileText, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import SummaryCardPDF from "./SummaryCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import remarkGfm from "remark-gfm";
import { Button, TextLinear } from "@core/index";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
const SummaryCard = ({
  content,
  imageUrl,
  videoUrl,
  createdAt,
  author,
  onCreateAgain,
  cefrLevel,
  contentLanguage,
  title,
}: {
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  createdAt: string;
  author: string;
  onCreateAgain: () => void;
  cefrLevel?: string;
  contentLanguage?: string;
  title?: string;
}) => {
  const finalImageUrl = useImagePdf(imageUrl);
  const { t } = useTranslation();
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  ) as any;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 p-px">
      <div className="p-6 rounded-[10px] bg-white dark:bg-gray-800">
        {imageUrl && (
          <div className="relative">
            <CardImage
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              createdAt={createdAt}
            />
            {/* CEFR Level Chip - Only show for English content */}
            {cefrLevel &&
              ["en", "fr", "de", "es"].includes(contentLanguage) && (
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-full shadow-lg border border-teal-700">
                  {cefrLevel}
                </div>
              )}
          </div>
        )}
        <div className="flex items-center justify-between gap-4 mb-6 mt-2">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400">
              <FileText size={24} />
            </div>
            <TextLinear
              variant={"h1"}
              from="from-teal-600 dark:from-teal-400"
              size="2xl"
              weight="bold"
              to="to-teal-500 dark:to-teal-300"
              className="h-20 sm:h-14 items-center justify-center flex"
            >
              summaries.details
            </TextLinear>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            {onCreateAgain && (
              <Button
                onClick={onCreateAgain}
                variant="solid"
                size="md"
                textColor="text-white dark:text-white"
                bgColor="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600"
                rounded="xl"
              >
                <RefreshCw size={18} />
                <span>{t("common.createAnother")}</span>
              </Button>
            )}

            <PdfDownloadButton
              pdfTitle={content.slice(0, 20)}
              openCustomizationModal={() => setShowPdfCustomizationModal(true)}
              document={
                <SummaryCardPDF
                  customizationSettings={customizationSettings}
                  content={content}
                  createdAt={createdAt}
                  author={author}
                  imageUrl={finalImageUrl}
                  title={title}
                />
              }
              btnTheme="bg-gradient-to-r from-teal-500 to-emerald-500"
            />
          </div>
        </div>

        <div
          className="prose dark:prose-invert max-w-none bg-white/50 dark:bg-gray-800/50 rounded-xl 
            prose-table:border-collapse prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 
            prose-td:p-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2
            prose-table:w-full prose-table:my-4"
          data-testid="summary-content"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="prose dark:prose-invert max-w-none bg-white/50 dark:bg-gray-800/50 rounded-xl 
              prose-table:border-collapse prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 
              prose-td:p-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2
              prose-table:w-full prose-table:my-4 prose-pre:whitespace-pre-wrap prose-pre:break-words  prose-pre:overflow-auto prose-pre:max-w-full"
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>

      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <SummaryCardPDF
            customizationSettings={customizationSettings}
            content={content}
            createdAt={createdAt}
            author={author}
            imageUrl={finalImageUrl}
            title={title}
          />
        }
      />
    </div>
  );
};

export default SummaryCard;
